import * as React from "react";

// ════════════════════════════════════════════════════════════════════════
//  Full Phosphor icon library (@phosphor-icons/react — the same library
//  nav-icons.jsx uses), loaded ON DEMAND as its own chunk.
//
//  The whole library is ~1,500 icons, far too big to ship in the main
//  bundle, so it is only downloaded the first time something needs it
//  (the module icon picker, or a sidebar module whose icon isn't one of
//  the few imported statically in nav-icons.jsx). After that it's cached.
//
//  Module icons are stored by kebab-case name — "shopping-cart",
//  "chart-pie", "gear-six" — the same style nav-icons.jsx already used.
// ════════════════════════════════════════════════════════════════════════

let library = null;          // { ShoppingCart: Component, … } once loaded
let loading = null;          // the in-flight import() promise
const listeners = new Set(); // components waiting for the library

// Exports of the package that are helpers, not icons.
const NOT_ICONS = new Set(["IconBase", "IconContext", "SSRBase", "SSR"]);

export const kebabToPascal = (name) =>
  String(name || "").split("-").filter(Boolean).map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("");

export const pascalToKebab = (name) =>
  String(name || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .toLowerCase();

// The library chunk is large (~6 MB, ~1 MB gzipped) — on a slow or flaky
// connection the download can fail. A failed attempt is forgotten and
// retried a few times (while something is still waiting for icons), so one
// bad download doesn't leave icons blank until the page is reloaded.
const MAX_LOAD_ATTEMPTS = 4;
const RETRY_DELAY_MS = 4000;
let failedAttempts = 0;

export const loadIconLibrary = () => {
  if (library) return Promise.resolve(library);
  if (!loading) {
    loading = import(/* webpackChunkName: "phosphor-icons-all" */ "@phosphor-icons/react").then((mod) => {
      library = mod;
      listeners.forEach((fn) => fn());
      return mod;
    }).catch((err) => {
      console.warn("[icons] icon library failed to load:", err?.message || err);
      loading = null;
      failedAttempts += 1;
      if (failedAttempts < MAX_LOAD_ATTEMPTS && listeners.size > 0) {
        setTimeout(loadIconLibrary, RETRY_DELAY_MS * failedAttempts);
      }
      return null;
    });
  }
  return loading;
};

export const getLoadedIconLibrary = () => library;

/** Re-renders the calling component once the library has loaded. */
export const useIconLibrary = () => {
  const [, force] = React.useState(0);
  React.useEffect(() => {
    if (library) return undefined;
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    loadIconLibrary();
    return () => listeners.delete(fn);
  }, []);
  return library;
};

/** Component for a kebab-case name from the loaded library (or undefined). */
export const libraryIcon = (name) => {
  if (!library || !name) return undefined;
  const key = kebabToPascal(name);
  if (NOT_ICONS.has(key)) return undefined;
  const C = library[key];
  return C && typeof C === "object" ? C : undefined;
};

let cachedNames = null;
/** Every icon in the library as kebab-case names, A→Z (empty until loaded). */
export const allIconNames = () => {
  if (!library) return [];
  if (!cachedNames) {
    cachedNames = Object.keys(library)
      .filter((k) => /^[A-Z]/.test(k) && !NOT_ICONS.has(k) && library[k] && typeof library[k] === "object")
      .map(pascalToKebab)
      .sort();
  }
  return cachedNames;
};

// ── Suggest an icon from a module name ──────────────────────────────────
// Keyword → icon (checked in order, first hit wins). Anything not covered
// falls back to an exact library match on one of the name's words
// (e.g. "Truck Delivery" → "truck"), then a neutral default.
const KEYWORDS = [
  [/dash ?board|overview|home/, "chart-pie"],
  [/order/, "shopping-cart"],
  [/product|inventory|catalog/, "package"],
  [/wordpress/, "globe"],
  [/shopify/, "shopping-bag"],
  [/store|shop/, "storefront"],
  [/master ?setting/, "squares-four"],
  [/setting|config|preference/, "gear-six"],
  [/module/, "squares-four"],
  [/permission|access/, "lock-key"],
  [/role/, "shield-check"],
  [/group|team/, "users-three"],
  [/customer|client|users|members/, "users"],
  [/account|profile|user/, "user-circle"],
  [/subscription|plan|billing|pricing/, "credit-card"],
  [/payout|wallet|withdraw/, "wallet"],
  [/bill|invoice|receipt/, "receipt"],
  [/balance/, "scales"],
  [/financ|revenue|profit|earning/, "trend-up"],
  [/payment|card/, "credit-card"],
  [/\bads?\b|advert|campaign|marketing|promo/, "megaphone"],
  [/social|share/, "share-network"],
  [/influencer/, "star"],
  [/\bai\b|assistant|bot|automation/, "sparkle"],
  [/integration|connect|plugin|api/, "plugs-connected"],
  [/report|analytic|statistic|insight/, "chart-bar"],
  [/notification|alert/, "bell"],
  [/message|chat|inbox/, "chat-circle"],
  [/mail|email|newsletter/, "envelope"],
  [/calendar|schedule|event/, "calendar"],
  [/history|log|activity/, "clock-counter-clockwise"],
  [/company|business|organi[sz]ation/, "buildings"],
  [/bank/, "bank"],
  [/help|support|faq/, "lifebuoy"],
  [/document|file|page/, "file-text"],
  [/image|media|gallery|photo/, "image"],
  [/video/, "video-camera"],
  [/ship|deliver|logistic/, "truck"],
  [/task|todo|checklist/, "check-square"],
  [/security|secure/, "shield"],
  [/search/, "magnifying-glass"],
  [/upload|import/, "upload-simple"],
  [/download|export/, "download-simple"],
];

export const DEFAULT_MODULE_ICON = "squares-four";

export const suggestIconForName = (moduleName) => {
  const text = String(moduleName || "").toLowerCase().trim();
  if (!text) return "";
  for (const [re, icon] of KEYWORDS) if (re.test(text)) return icon;
  if (library) {
    const names = new Set(allIconNames());
    const words = text.split(/[^a-z0-9]+/).filter((w) => w.length > 2);
    for (const w of words) {
      if (names.has(w)) return w;
      const singular = w.replace(/(ies)$/, "y").replace(/s$/, "");
      if (names.has(singular)) return singular;
    }
  }
  return DEFAULT_MODULE_ICON;
};
